<script setup lang="ts">
  import { useIntersectionObserver, useWindowSize } from '@vueuse/core'
  import { usePortalGlobalSearch } from '~/composables/api/search/usePortalGlobalSearch'
  import {
    PORTAL_SEARCH_ENTITY_TYPES,
    getPortalSearchEntityIcon,
    getPortalSearchEntityLabelKey,
    resolvePortalSearchLocation,
    type PortalSearchEntityType,
    type PortalSearchResult,
  } from '~/utils/portalGlobalSearch'
  import { numberFormat } from '~/utils/textFormatting'

  const { locale, t } = useI18n()
  const localePath = useLocalePath()
  const route = useRoute()
  const router = useRouter()
  const searchTerm = ref('')
  const selectedTypes = ref<PortalSearchEntityType[]>([])
  const favouritesOnly = ref(false)
  const isOpen = ref(false)
  const rootRef = ref<HTMLElement | null>(null)
  const viewportRef = ref<HTMLElement | null>(null)
  const loadMoreRef = ref<HTMLElement | null>(null)
  const { width } = useWindowSize()
  const isMobile = computed(() => width.value < 1280)

  const {
    results,
    total,
    facetCounts,
    favouriteCount,
    hasFavourites,
    isLoading,
    isFetchingFacets,
    isFetchingNextPage,
    error,
    hasNextPage,
    fetchNextPage,
    retry,
  } = usePortalGlobalSearch({
    searchTerm,
    language: locale,
    selectedTypes,
    favouritesOnly,
    enabled: isOpen,
  })

  const dropdownClasses = computed(() =>
    isMobile.value
      ? 'fixed inset-x-3 top-20 bottom-4 rounded-2xl border-neutral-200 shadow-2xl'
      : 'absolute left-0 right-0 mt-3 max-h-[28rem] rounded-xl border-neutral-300 shadow-xl',
  )
  const resultStatus = computed(() => {
    if (isLoading.value) return t('globalsearch.loading')
    if (error.value) return t('globalsearch.error')

    return t('globalsearch.resultsCount', {
      count: total.value ?? results.value.length,
    })
  })
  const hasActiveFilters = computed(() => favouritesOnly.value || selectedTypes.value.length > 0)

  const toggleType = (type: PortalSearchEntityType) => {
    selectedTypes.value = selectedTypes.value.includes(type)
      ? selectedTypes.value.filter((selectedType) => selectedType !== type)
      : [...selectedTypes.value, type]
  }

  const clearSearch = () => {
    searchTerm.value = ''
  }

  const clearFilters = async () => {
    selectedTypes.value = []
    favouritesOnly.value = false

    await nextTick()
    rootRef.value?.querySelector<HTMLInputElement>('input[role="combobox"]')?.focus()
  }

  const handleEscape = () => {
    if (searchTerm.value) clearSearch()
  }

  const selectResult = async (item: PortalSearchResult) => {
    isOpen.value = false
    await router.push(resolvePortalSearchLocation(item, localePath, route.fullPath, locale.value))
  }

  useIntersectionObserver(
    loadMoreRef,
    ([entry]) => {
      if (
        entry?.isIntersecting &&
        hasNextPage.value &&
        !isLoading.value &&
        !isFetchingNextPage.value
      ) {
        void fetchNextPage()
      }
    },
    {
      root: viewportRef,
      threshold: 0.1,
    },
  )
</script>

<template>
  <div ref="rootRef" class="relative w-full max-w-full md:max-w-2xl">
    <ComboboxRoot v-model:open="isOpen" v-model:search-term="searchTerm" class="relative">
      <ComboboxAnchor
        class="relative flex items-center rounded-full border border-neutral-300 bg-neutral-100 px-4 py-2 transition focus-within:border-primary"
        @click="isOpen = true"
      >
        <img
          src="/icons/search-glass-primary.svg"
          alt=""
          class="pointer-events-none relative z-10 h-5 w-5 shrink-0 opacity-80"
          aria-hidden="true"
        />
        <ComboboxInput
          class="absolute inset-0 z-0 h-full w-full rounded-full bg-transparent px-12 text-sm leading-6 text-neutral-800 placeholder:text-neutral-500 focus:outline-none"
          :placeholder="t('globalsearch.search')"
          :aria-label="t('globalsearch.search')"
        />
        <div class="relative z-10 ml-auto flex h-5 w-5 shrink-0 items-center justify-center">
          <Icon
            v-if="isLoading"
            name="mdi:loading"
            class="h-4 w-4 animate-spin text-primary duration-500"
            aria-hidden="true"
          />
          <button
            v-else-if="searchTerm"
            type="button"
            class="flex h-5 w-5 items-center justify-center rounded-full text-neutral-400 hover:text-neutral-600 focus:outline-none focus:ring-4 focus:ring-focused"
            :aria-label="t('globalsearch.clear')"
            @mousedown.prevent
            @click.stop="clearSearch"
          >
            <img src="/icons/close-icon-primary.svg" alt="" class="h-4 w-4" />
          </button>
        </div>
      </ComboboxAnchor>

      <div
        v-if="isOpen"
        class="fixed inset-x-0 top-20 bottom-0 z-40 bg-black/20 backdrop-blur-[1px] lg:hidden"
        aria-hidden="true"
        @click="isOpen = false"
      />

      <ComboboxContent
        class="z-50 flex flex-col overflow-hidden border bg-white"
        :class="dropdownClasses"
        @escape-key-down="handleEscape"
      >
        <p class="sr-only" role="status" aria-live="polite" aria-atomic="true">
          {{ resultStatus }}
        </p>

        <div
          class="flex flex-wrap gap-2 border-b border-neutral-100 px-3 pt-3 pb-2"
          role="group"
          :aria-label="t('globalsearch.filtersLabel')"
        >
          <button
            type="button"
            class="flex items-center gap-2 rounded-full border px-3 py-1.5 text-xs transition"
            :class="
              favouritesOnly
                ? 'border-primary bg-primary/10 text-primary'
                : hasFavourites
                  ? 'border-neutral-200 text-neutral-600 hover:border-neutral-300'
                  : 'cursor-not-allowed border-neutral-200 text-neutral-400'
            "
            :disabled="!hasFavourites"
            :aria-pressed="favouritesOnly"
            @click="favouritesOnly = !favouritesOnly"
          >
            <Icon name="ic:round-star" class="h-4 w-4" aria-hidden="true" />
            <span>{{ t('globalsearch.filters.favourites') }}</span>
            <span
              class="rounded-full px-2 py-0.5 text-[11px] font-semibold"
              :class="
                favouritesOnly ? 'bg-white/60 text-primary' : 'bg-neutral-100 text-neutral-600'
              "
            >
              {{ numberFormat(favouriteCount) }}
            </span>
          </button>

          <button
            v-for="type in PORTAL_SEARCH_ENTITY_TYPES"
            :key="type"
            type="button"
            class="flex items-center gap-2 rounded-full border px-3 py-1.5 text-xs transition"
            :class="
              selectedTypes.includes(type)
                ? 'border-primary bg-primary/10 text-primary'
                : 'border-neutral-200 text-neutral-600 hover:border-neutral-300'
            "
            :aria-pressed="selectedTypes.includes(type)"
            @click="toggleType(type)"
          >
            <Icon :name="getPortalSearchEntityIcon(type)" class="h-4 w-4" aria-hidden="true" />
            <span>{{ t(getPortalSearchEntityLabelKey(type)) }}</span>
            <span
              class="relative inline-flex w-12 items-center justify-center rounded-full px-2 py-0.5 text-[11px] font-semibold"
              :class="
                selectedTypes.includes(type)
                  ? 'bg-white/60 text-primary'
                  : 'bg-neutral-100 text-neutral-600'
              "
            >
              <Icon
                v-if="isFetchingFacets && facetCounts[type] == null"
                name="mdi:loading"
                class="absolute inset-0 m-auto h-3.5 w-3.5 animate-spin"
                aria-hidden="true"
              />
              <span :class="{ invisible: isFetchingFacets && facetCounts[type] == null }">
                {{ numberFormat(facetCounts[type] ?? 0) }}
              </span>
            </span>
          </button>
        </div>

        <div
          class="flex flex-wrap items-center gap-3 border-b border-neutral-100 px-4 py-3 text-xs font-semibold text-neutral-500 uppercase"
        >
          <span data-testid="search-results-summary" class="flex items-center gap-2">
            {{ t('globalsearch.searchResults') }}
            <span
              data-testid="search-result-count"
              class="relative inline-flex w-12 items-center justify-center rounded-full bg-neutral-100 px-2 py-0.5 text-[11px] font-semibold text-neutral-600"
            >
              <Icon
                v-if="isLoading && total === null"
                name="mdi:loading"
                class="absolute inset-0 m-auto h-3.5 w-3.5 animate-spin"
                aria-hidden="true"
              />
              <span :class="{ invisible: isLoading && total === null }">
                {{ numberFormat(total ?? 0) }}
              </span>
            </span>
          </span>
          <button
            v-if="hasActiveFilters"
            type="button"
            class="flex items-center gap-1.5 rounded text-xs font-semibold text-primary normal-case hover:text-primary-light focus:outline-none focus:ring-4 focus:ring-focused"
            :aria-label="t('globalsearch.clearFilters')"
            @click="clearFilters"
          >
            <Icon name="mdi:close" class="h-4 w-4" aria-hidden="true" />
            {{ t('globalsearch.clearFilters') }}
          </button>
          <span class="ml-auto hidden text-[11px] font-normal text-neutral-400 md:block">
            {{ t('globalsearch.closeHint') }}
          </span>
          <button
            type="button"
            class="ml-auto text-[11px] font-semibold text-neutral-500 hover:text-neutral-700 md:hidden"
            @click="isOpen = false"
          >
            {{ t('globalsearch.close') }}
          </button>
        </div>

        <div
          v-if="isLoading"
          class="flex min-h-64 flex-1 flex-col items-center justify-center gap-2 text-sm text-neutral-500"
        >
          <Icon
            name="mdi:loading"
            class="h-6 w-6 animate-spin text-primary duration-500"
            aria-hidden="true"
          />
          {{ t('globalsearch.loading') }}
        </div>
        <div v-else-if="error" class="flex items-center justify-between gap-4 p-4 text-sm">
          <span class="text-critical">{{ t('globalsearch.error') }}</span>
          <button
            type="button"
            class="rounded border border-primary px-3 py-1.5 font-semibold text-primary hover:bg-primary/10"
            @click="retry"
          >
            {{ t('globalsearch.retry') }}
          </button>
        </div>
        <div
          v-else-if="!results.length"
          class="flex min-h-64 flex-1 flex-col items-center justify-center px-6 text-center"
        >
          <h3 class="mb-2 text-sm font-semibold text-neutral-800">
            {{ t('error.noResultsFound') }}
          </h3>
          <p class="max-w-sm text-xs text-neutral-400">
            {{ t('error.youCanTryADifferentSearch') }}
          </p>
        </div>

        <ComboboxViewport v-else as-child>
          <div ref="viewportRef" class="min-h-0 divide-y divide-neutral-100">
            <ComboboxItem
              v-for="item in results"
              :key="item.id"
              :value="item"
              class="cursor-pointer group p-4 transition data-[highlighted]:bg-neutral-100 data-[highlighted]:outline-none hover:bg-neutral-50"
              @select.prevent="selectResult(item)"
            >
              <div class="flex items-start gap-3">
                <div class="relative h-9 w-9 shrink-0">
                  <div
                    class="flex h-full w-full items-center justify-center rounded-full bg-neutral-100 text-neutral-600"
                  >
                    <Icon
                      :name="getPortalSearchEntityIcon(item.entityType)"
                      class="h-5 w-5"
                      aria-hidden="true"
                    />
                  </div>
                  <span
                    v-if="item.isFavourite"
                    class="absolute -right-1 -bottom-1 flex h-4 w-4 items-center justify-center text-primary"
                    role="img"
                    :aria-label="t('favourites.button.favourite')"
                  >
                    <Icon name="ic:round-star" class="h-4 w-4" aria-hidden="true" />
                  </span>
                </div>
                <div class="flex w-full flex-col gap-1">
                  <div class="flex items-center gap-2">
                    <div class="line-clamp-1 text-sm font-semibold text-neutral-800">
                      <PortalSearchHighlight :text="item.highlightTitle ?? item.title" />
                    </div>
                    <span
                      class="rounded-full bg-primary/10 px-2 py-0.5 text-[11px] font-semibold text-primary shrink-0"
                    >
                      {{ t(getPortalSearchEntityLabelKey(item.entityType)) }}
                    </span>
                  </div>
                  <div
                    v-if="item.summary || item.highlightSummary"
                    class="line-clamp-2 text-xs text-neutral-500"
                  >
                    <PortalSearchHighlight :text="item.highlightSummary ?? item.summary" />
                  </div>
                  <div v-if="item.tags?.length" class="flex flex-wrap gap-1">
                    <span
                      v-for="tag in item.tags"
                      :key="tag"
                      class="rounded-full bg-neutral-100 px-2 py-0.5 text-[11px] text-neutral-600 group-hover:bg-neutral-200/10"
                    >
                      {{ tag }}
                    </span>
                  </div>
                </div>
              </div>
            </ComboboxItem>

            <div
              v-if="hasNextPage && !isLoading && !error"
              ref="loadMoreRef"
              role="status"
              aria-live="polite"
            >
              <div class="py-4 text-center text-sm text-neutral-500">
                {{ t('globalsearch.loading') }}
              </div>
              <div class="relative h-[3px] w-full overflow-hidden">
                <div
                  class="absolute h-full w-full animate-loading-line bg-gradient-to-r from-transparent via-current to-transparent via-[20%] to-[80%] text-primary"
                />
              </div>
            </div>
          </div>
        </ComboboxViewport>
      </ComboboxContent>
    </ComboboxRoot>
  </div>
</template>
